import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Cart, CartItem, CartSummary, SupplierOrder } from '../models/cart.models';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private cart: Cart = {
    id: 'cart-1',
    items: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  private cartSubject = new BehaviorSubject<Cart>(this.cart);
  private supplierOrders: SupplierOrder[] = [];

  constructor() {
    // Load saved cart from localStorage
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      this.cart = JSON.parse(savedCart);
      this.cartSubject.next(this.cart);
    }
  }

  getCart(): Observable<Cart> {
    return this.cartSubject.asObservable();
  }

  getCartSummary(): Observable<CartSummary> {
    return this.cartSubject.pipe(
      map((cart) => ({
        totalItems: cart.items.length,
        supplierCount: new Set(cart.items.map((item) => item.supplierId)).size,
      })),
    );
  }

  addToCart(item: Omit<CartItem, 'id' | 'addedAt'>): void {
    const existingItem = this.cart.items.find(
      (i) => i.materialId === item.materialId && i.supplierId === item.supplierId,
    );

    if (existingItem) {
      existingItem.quantity += item.quantity;
    } else {
      const newItem: CartItem = {
        ...item,
        id: crypto.randomUUID(),
        addedAt: new Date(),
      };
      this.cart.items.push(newItem);
    }

    this.updateCart();
  }

  removeFromCart(itemId: string): void {
    this.cart.items = this.cart.items.filter((item) => item.id !== itemId);
    this.updateCart();
  }

  updateQuantity(itemId: string, quantity: number): void {
    const item = this.cart.items.find((i) => i.id === itemId);
    if (item) {
      item.quantity = quantity;
      this.updateCart();
    }
  }

  clearCart(): void {
    this.cart.items = [];
    this.updateCart();
  }

  private updateCart(): void {
    this.cart.updatedAt = new Date();

    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify(this.cart));

    // Notify subscribers
    this.cartSubject.next(this.cart);
  }

  createSupplierOrders(): SupplierOrder[] {
    // Group items by supplier
    const supplierGroups = this.cart.items.reduce(
      (groups, item) => {
        if (!groups[item.supplierId]) {
          groups[item.supplierId] = {
            supplierId: item.supplierId,
            supplierName: item.supplierName,
            orderNumber: this.generateOrderNumber(),
            items: [],
            status: 'pending' as const,
            createdAt: new Date(),
          };
        }
        groups[item.supplierId].items.push(item);
        return groups;
      },
      {} as { [key: string]: SupplierOrder },
    );

    const orders = Object.values(supplierGroups);
    this.supplierOrders.push(...orders);

    // Clear cart after creating orders
    this.clearCart();

    return orders;
  }

  private generateOrderNumber(): string {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const random = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, '0');
    return `PO-${date}-${random}`;
  }

  getSupplierOrders(): SupplierOrder[] {
    return this.supplierOrders;
  }

  updateOrderStatus(orderNumber: string, status: SupplierOrder['status']): void {
    const order = this.supplierOrders.find((o) => o.orderNumber === orderNumber);
    if (order) {
      order.status = status;
    }
  }
}
